import { useEffect, useState, useContext } from 'react'
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { useCourseEngagement } from '@blend-ed/blendx-platform/hooks';
import { AppContext } from '@edx/frontend-platform/react';
import { Button, ButtonGroup, IconButton } from '@blend-ed/blendx-ui';
import { format } from 'date-fns';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import messages from '../../../components/messages';

const colors = ['--primary', '--warning', '--pink', '--success', '--info', '--danger', '--secondary', '--yellow', '--purple'];

function CourseEngagementChart({ intl }) {
    const { authenticatedUser } = useContext(AppContext)
    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

    const [endDate, setEndDate] = useState(new Date());
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 6)));
    const [labels, setLabels] = useState(getLabels(endDate));
    const [datasets, setDatasets] = useState([]);

    const { data: courseEngagement, isLoading } = useCourseEngagement(
        authenticatedUser.id ? authenticatedUser.id : null,
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd')
    );

    //? chatdata setting useEffect
    useEffect(() => {
        if (courseEngagement) {
            setDatasets(courseEngagement.map((dataset, index) => {
                return {
                    label: dataset.course_name,
                    data: dataset.engagement,
                    backgroundColor: getComputedStyle(document.documentElement).getPropertyValue(colors[index]),
                    barThickness: 32,
                };
            }));
        } else {
            setDatasets([]);
        }
    }, [courseEngagement]);

    //? auto calculate the starDate when the endDate is changed
    useEffect(() => {
        const date = new Date(endDate);
        date.setDate(date.getDate() - 6);
        setStartDate(date);

        // set the labels based on the new endDate
        setLabels(getLabels(endDate));
    }, [endDate]);

    //? this function is used to diable the next button in the date range
    const canGoForward = () => {
        const today = new Date();
        const lastDate = new Date(endDate);
        lastDate.setDate(lastDate.getDate() + 6);
        return today < lastDate;
    }

    //? this function is used to change the date range
    const changeDate = (seek) => {
        // function gets 'back' or 'forward' or 'current' as argument
        if (seek === 'back') {
            const newDate = new Date(endDate);
            newDate.setDate(newDate.getDate() - 6);
            setEndDate(newDate);
        }

        if (seek === 'forward') {
            const newDate = new Date(endDate);
            newDate.setDate(newDate.getDate() + 6);
            setEndDate(newDate);
        }

        if (seek === 'current') {
            setEndDate(new Date());
        }
    }

    //? reutrns an array with the date in the format 'dd-MMM' for the last 6 days from a given date
    function getLabels(currentDate) {
        // based on the current selected date the dateRange array will be created
        const dateRange = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(currentDate);
            date.setDate(date.getDate() - i);
            return date;
        }).reverse();

        // from the dateRange array crate a labels array with the date in the format 'dd-MMM'
        return dateRange.map(date => date.toLocaleString('en-us', { day: '2-digit', month: 'short' }));
    }

    //? this is the options for the chart
    const options = {
        plugins: {
            title: {
                display: false,
            },
            tooltip: {
                mode: 'index',
                intersect: false,
            },
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 12,
                    boxHeight: 12,
                    useBorderRadius: true,
                    borderRadius: 2,
                    padding: 24,
                },
            },
        },
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 5 / 1,
        scales: {
            x: {
                beginAtZero: true,
                stacked: true,
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                },
                border: {
                    display: false,
                    dash: [4, 4],
                },
                grid: {
                    tickBorderDash: [4, 4],
                    borderDash: [5, 5], // Make horizontal grid lines dotted
                },
            },
        },
    };

    return (

        <div className='engagement-chart__card'>
            <div className="engagement-chart__header">
                <div className="engagement-chart__left">
                    <div className="engagement-chart__title">{intl.formatMessage(messages.courseEngagement)}</div>
                </div>
                <div className="completion-chart__right">
                    <ButtonGroup size="xs" vertical={false}>
                        <IconButton
                            icon="arrow-drop-left"
                            size="xs"
                            onClick={() => changeDate('back')}
                            variant="outline-gray"
                        />
                        <Button size="xs" variant="outline-gray" onClick={() => changeDate('current')}>
                            {intl.formatMessage(messages.today)}
                        </Button>
                        <IconButton
                            icon="arrow-drop-right"
                            size="xs"
                            onClick={() => changeDate('forward')}
                            variant="outline-gray"
                            disabled={canGoForward()}
                        />
                    </ButtonGroup>
                </div>
            </div>
            <div className='engagement-chart-container'>
                <Bar data={{ labels, datasets }} options={options} />
            </div>
        </div>

    )
}

CourseEngagementChart.propTypes = {
    intl: intlShape.isRequired,
};

export default injectIntl(CourseEngagementChart);
